import useMessageRefresh from "../hooks/useMessageRefresh";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Send, MessageCircle, Loader2, User,
} from "lucide-react";
import { Button } from "../components/ui/button";
import CalmEmptyState from "../components/learner/CalmEmptyState";
import ReshuffleConsentPanel from "../components/diary/ReshuffleConsentPanel";
import PupilAvailability from "../components/learner/PupilAvailability";

const API = process.env.REACT_APP_BACKEND_URL || "";

/** LearnerMessages — conversation list + single-thread view for learners. */
export default function LearnerMessages() {
  const { threadId } = useParams();
  if (threadId) return <ThreadView key={threadId} threadId={threadId} />;
  return <ThreadsList />;
}


function ThreadsList() {
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");
  const startLock = useRef(false);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const acceptInstructors = useCallback(data => {
    if (!Array.isArray(data?.instructors)) throw new Error("Invalid instructor list");
    setInstructors(data.instructors);
  }, []);
  const { loading: instructorsLoading, error: instructorsError, load: refreshInstructors } = useMessageRefresh(`${API}/api/learner-messages/instructors`, acceptInstructors);
  const start = async instructorId => {
    if (startLock.current) return;
    startLock.current = true; setStarting(true); setStartError("");
    try {
      const { data } = await axios.post(`${API}/api/learner-messages/start`, { instructor_id: instructorId }, { withCredentials: true });
      if (!mounted.current) return;
      if (!data?.thread?.id) throw new Error("Conversation unconfirmed");
      nav(`/learn/messages/${encodeURIComponent(data.thread.id)}`);
    } catch {
      if (mounted.current) setStartError("The conversation could not open. You can try again when you are ready.");
    } finally {
      startLock.current = false;
      if (mounted.current) setStarting(false);
    }
  };
  const accept = useCallback(data => {
    if (!Array.isArray(data?.threads)) throw new Error("Invalid conversation list");
    setThreads(data.threads);
  }, []);
  const { loading, error, load } = useMessageRefresh(`${API}/api/learner-messages`, accept);

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => nav("/learn")}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">Messages</p>
            <p className="text-[10px] text-slate-400">Your instructor conversations</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4">
        <ReshuffleConsentPanel mode="learner" />
        {instructorsError && <div role="alert"><p>Instructor details could not refresh.</p><button type="button" onClick={refreshInstructors} className="min-h-11 underline">Refresh instructor details</button></div>}
        {!instructorsLoading && !instructorsError && instructors.length === 0 && <p className="text-sm text-slate-500">There is no current instructor linked for messaging.</p>}
        {!instructorsError && instructors.map(instructor => <button key={instructor.instructor_id} type="button" disabled={starting} onClick={() => start(instructor.instructor_id)} className="min-h-11 w-full text-left font-semibold text-blue-700 py-2">
          {instructors.length === 1 ? "Message your instructor" : `Message ${instructor.instructor_name}`}
        </button>)}
        {startError && <p role="alert">{startError}</p>}
        {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>}
        {error && <div role="alert"><p>{error}</p><button type="button" onClick={load} className="min-h-11 underline">Refresh messages</button></div>}
        {!loading && !error && threads.length === 0 && (
          <CalmEmptyState
            testid="no-threads"
            tone="nothing-yet"
            title="No messages yet"
            body={instructors.length ? "You can start a conversation with your instructor here." : "Your conversations will appear here."}
          />
        )}
        {!loading && threads.length > 0 && (
          <div className="space-y-2">
            {threads.map(t => (
              <button key={t.id} data-testid={`thread-${t.id}`}
                onClick={() => nav(`/learn/messages/${t.id}`)}
                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-3 flex items-center gap-3 hover:border-blue-300 active:scale-[0.98] transition-all text-left">
                <div className="w-11 h-11 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">{t.instructor_name || "Instructor"}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {t.last_message_preview || "No messages yet"}
                  </p>
                </div>
                {t.last_message_at && (
                  <span className="text-[10px] text-slate-400 flex-shrink-0">
                    {new Date(t.last_message_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ThreadView({ threadId }) {
  const nav = useNavigate();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [historyTruncated, setHistoryTruncated] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const scrollRef = useRef(null);
  const locked = useRef(false);
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const accept = useCallback(data => {
    if (data?.thread?.id !== threadId || !Array.isArray(data.messages)) throw new Error("Conversation mismatch");
    setThread(data.thread);
    setMessages(data.messages); setHistoryTruncated(Boolean(data.history_truncated));
  }, [threadId]);
  const { loading, error, load } = useMessageRefresh(`${API}/api/learner-messages/${encodeURIComponent(threadId)}`, accept);
  const lastMessageId = messages[messages.length - 1]?.id;
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lastMessageId]);

  const send = async () => {
    if (!input.trim() || locked.current || !thread) return;
    const text = input.trim();
    locked.current = true;
    setSending(true); setSendError("");
    try {
      const { data } = await axios.post(`${API}/api/learner-messages/${encodeURIComponent(threadId)}/send`, { body: text }, { withCredentials: true });
      if (!mounted.current) return;
      if (!data?.id || data.thread_id !== threadId || data.body !== text || data.direction !== "inbound" || !data.sent_at) throw new Error("Send unconfirmed");
      setMessages(current => current.some(message => message.id === data.id) ? current : [...current, data]);
      setInput("");
      await load();
    } catch {
      if (mounted.current) setSendError("I couldn’t confirm your message was sent. Your words are still here. Refresh the conversation before trying again, in case it arrived.");
    } finally {
      locked.current = false;
      if (mounted.current) setSending(false);
    }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex flex-col">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="back-to-threads" onClick={() => nav("/learn/messages")}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <User className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">
              {thread?.instructor_name || "Instructor"}
            </p>
          </div>
        </div>
      </header>

      {thread && <div className="px-4"><PupilAvailability key={threadId} threadId={threadId} /></div>}
      <div ref={scrollRef} data-testid="messages-list" className="flex-1 overflow-y-auto max-w-lg mx-auto w-full px-4 py-4 space-y-2">
        {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>}
        {error && <div role="alert"><p>{error}</p><button type="button" onClick={load} className="min-h-11 underline">Refresh messages</button></div>}
        {!loading && !error && messages.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-10">No messages yet. Say hi!</p>
        )}
        {historyTruncated && <p className="text-sm text-slate-500">Showing the most recent messages. Earlier messages are retained.</p>}
        {messages.map(m => {
          const outbound = m.direction === "outbound"; // from instructor to learner
          return (
            <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${outbound ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${
                outbound
                  ? "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 rounded-bl-sm"
                  : "bg-blue-600 text-white rounded-br-sm"
              }`}>
                <p className="leading-relaxed whitespace-pre-wrap">{m.body}</p>
                <p className={`text-[9px] mt-1 ${outbound ? "text-slate-400" : "text-blue-200"}`}>
                  {new Date(m.sent_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {sendError && <div role="alert" className="px-4 py-2"><p>{sendError}</p><button type="button" onClick={load} className="min-h-11 underline">Check conversation</button></div>}
      <div className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 sticky bottom-0 pb-safe">
        <div className="max-w-lg mx-auto px-3 py-2 flex items-end gap-2">
          <textarea data-testid="message-input" aria-label="Your message" disabled={sending || !thread} value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Write a message..."
            rows={1}
            className="flex-1 min-h-[40px] max-h-32 border border-slate-200 dark:border-slate-600 rounded-2xl px-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-700 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          <Button data-testid="send-btn" onClick={send} aria-label="Send message" disabled={sending || !thread || !input.trim()}
            className="h-10 w-10 p-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex-shrink-0">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

